import React, { useState, memo, useEffect } from "react";
import { Search, AlertCircle } from "lucide-react";
import { useDebounce } from "../../utils/debounce";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  debounceDelay?: number;
}

const validateSearchInput = (
  input: string
): { isValid: boolean; error?: string } => {
  if (!input.trim()) {
    return { isValid: true };
  }

  if (input.length > 100) {
    return {
      isValid: false,
      error: "Search term must be 100 characters or less",
    };
  }

  return { isValid: true };
};

const SearchInput: React.FC<SearchInputProps> = memo(
  ({
    value,
    onChange,
    placeholder = "Search...",
    label,
    className = "",
    debounceDelay,
  }) => {
    const [validationError, setValidationError] = useState<string>("");
    const [internalValue, setInternalValue] = useState<string>(value);

    const debouncedValue = useDebounce(internalValue, debounceDelay || 0);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    useEffect(() => {
      if (debounceDelay && debouncedValue !== value) {
        const validation = validateSearchInput(debouncedValue);
        if (validation.isValid) {
          setValidationError("");
          onChange(debouncedValue);
        } else {
          setValidationError(validation.error || "");
        }
      }
    }, [debouncedValue, debounceDelay, onChange, value]);

    const handleInputChange = (newValue: string) => {
      const validation = validateSearchInput(newValue);
      setInternalValue(newValue);

      if (!debounceDelay) {
        if (validation.isValid) {
          setValidationError("");
          onChange(newValue);
        } else {
          setValidationError(validation.error || "");
          onChange(newValue);
        }
      } else {
        if (!validation.isValid) {
          setValidationError(validation.error || "");
        } else {
          setValidationError("");
        }
      }
    };

    return (
      <div className={className}>
        {/* Label */}
        {label && (
          <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-2">
            {label}
          </label>
        )}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-tertiary)] w-3.5 h-3.5" />
            <input
              value={internalValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={placeholder}
              className={`w-full pl-8 pr-4 h-[38px] text-[13px] border border-[var(--color-border-primary)] rounded-[9px] transition-all duration-200 placeholder:text-[var(--color-text-tertiary)] bg-[var(--color-bg-base)] outline-none ${
                validationError
                  ? "border-[var(--color-error-dark)] focus:border-[var(--color-error-dark)]"
                  : "focus:border-[var(--color-primary-500)]"
              }`}
              maxLength={100}
            />
            {validationError && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <AlertCircle className="w-4 h-4 text-[var(--color-error-dark)]" />
              </div>
            )}
          </div>
        </div>
        {validationError && (
          <div className="mt-2 text-[13px] text-[var(--color-error-dark)] flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {validationError}
          </div>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
